import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import {
  MagnifyingGlass,
  DownloadSimple,
  PencilSimple,
  CheckCircle,
  XCircle,
  Trash,
  X,
  ArrowsClockwise,
  CaretLeft,
  CaretRight,
  CaretUp,
  CaretDown,
} from '@phosphor-icons/react';
import EditPodModal from '../components/pods/EditPodModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const PAGE_SIZE = 50;

const SortHeader = ({ label, field, sortConfig, onSort, align }) => {
  const active = sortConfig.field === field;
  const dir = active ? sortConfig.dir : null;
  return (
    <th
      className={`px-3 py-2.5 text-slate-600 font-semibold text-xs whitespace-nowrap cursor-pointer select-none hover:bg-slate-100 transition-colors ${align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : 'text-left'}`}
      onClick={() => onSort(field)}
      data-testid={`sort-${field}`}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        <span className="inline-flex flex-col leading-none">
          <CaretUp size={10} weight={active && dir === 'asc' ? 'bold' : 'regular'} className={active && dir === 'asc' ? 'text-[#0066FF]' : 'text-slate-300'} />
          <CaretDown size={10} weight={active && dir === 'desc' ? 'bold' : 'regular'} className={active && dir === 'desc' ? 'text-[#0066FF]' : 'text-slate-300'} />
        </span>
      </span>
    </th>
  );
};

const PODActivePage = () => {
  const { axiosInstance } = useAuth();
  const [pods, setPods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [editPod, setEditPod] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [importHistory, setImportHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [filterProvince, setFilterProvince] = useState('');
  const [filterUsage, setFilterUsage] = useState('');
  const [sortConfig, setSortConfig] = useState({ field: 'pod_code', dir: 'asc' });

  const fetchPods = useCallback(async () => {
    try {
      const { data } = await axiosInstance.get('/pods?status=active&limit=10000');
      setPods(data);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  }, [axiosInstance]);

  useEffect(() => { fetchPods(); }, [fetchPods]);

  const fetchHistory = useCallback(async () => {
    try {
      const { data } = await axiosInstance.get('/pods/import-history');
      setImportHistory(data);
      setShowHistory(true);
    } catch (err) { toast.error('Errore caricamento storico'); }
  }, [axiosInstance]);

  const handleExport = () => window.open(`${API_URL}/api/pods/export?status=active`, '_blank');

  const handleRollback = async (batchId, filename) => {
    if (!window.confirm(`Confermi annullamento import "${filename}"?`)) return;
    try {
      const { data } = await axiosInstance.delete(`/pods/batch/${batchId}`);
      toast.success(`${data.deleted} POD eliminati`);
      fetchPods(); fetchHistory();
    } catch (err) { toast.error(err.response?.data?.detail || 'Errore'); }
  };

  const handleToggleRCU = async (podId) => {
    try {
      const { data } = await axiosInstance.patch(`/pods/${podId}/verify-rcu`);
      setPods(prev => prev.map(p => p.id === podId ? { ...p, verified_rcu: data.verified_rcu } : p));
    } catch (err) { toast.error('Errore aggiornamento RCU'); }
  };

  const openEdit = (pod) => {
    setEditPod(pod);
    setEditForm({
      pod_code: pod.pod_code, customer_name: pod.customer_name,
      customer_vat: pod.customer_vat || '', customer_tax_code: pod.customer_tax_code || '',
      province: pod.province, toponimo: pod.toponimo || '', indirizzo: pod.indirizzo || '',
      civico: pod.civico || '', cap: pod.cap || '', comune: pod.comune || '',
      codice_istat: pod.codice_istat || '', usage_type: pod.usage_type,
      annual_consumption_kwh: pod.annual_consumption_kwh, activation_date: pod.activation_date || '',
      potenza_impegnata_kw: pod.potenza_impegnata_kw ?? 3.0,
      residenza_anagrafica: pod.residenza_anagrafica ?? true,
      processo_esente_art21: pod.processo_esente_art21 || null,
      illuminazione_pubblica: pod.illuminazione_pubblica || false,
    });
  };

  const handleSaveEdit = async () => {
    setSaving(true);
    try {
      await axiosInstance.put(`/pods/${editPod.id}`, editForm);
      toast.success('POD aggiornato');
      setEditPod(null); fetchPods();
    } catch (err) { toast.error(err.response?.data?.detail || 'Errore'); }
    finally { setSaving(false); }
  };

  const handleSort = (field) => {
    setSortConfig(prev => ({
      field,
      dir: prev.field === field && prev.dir === 'asc' ? 'desc' : 'asc'
    }));
    setCurrentPage(1);
  };

  const provinces = useMemo(() => [...new Set(pods.map(p => p.province))].filter(Boolean).sort(), [pods]);

  const filteredAndSorted = useMemo(() => {
    let result = pods.filter(p => {
      const s = searchTerm.toLowerCase();
      const matchSearch = !s ||
        (p.pod_code || '').toLowerCase().includes(s) ||
        (p.customer_name || '').toLowerCase().includes(s) ||
        (p.comune || '').toLowerCase().includes(s);
      return matchSearch && (!filterProvince || p.province === filterProvince) && (!filterUsage || p.usage_type === filterUsage);
    });
    const { field, dir } = sortConfig;
    result.sort((a, b) => {
      let va = a[field] ?? '', vb = b[field] ?? '';
      if (typeof va === 'number' && typeof vb === 'number') return dir === 'asc' ? va - vb : vb - va;
      va = String(va).toLowerCase(); vb = String(vb).toLowerCase();
      if (va < vb) return dir === 'asc' ? -1 : 1;
      if (va > vb) return dir === 'asc' ? 1 : -1;
      return 0;
    });
    return result;
  }, [pods, searchTerm, filterProvince, filterUsage, sortConfig]);

  const paginatedPods = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return filteredAndSorted.slice(start, start + PAGE_SIZE);
  }, [filteredAndSorted, currentPage]);

  const totalPages = Math.ceil(filteredAndSorted.length / PAGE_SIZE);

  const totals = useMemo(() => ({
    consumption: filteredAndSorted.reduce((s, p) => s + (p.annual_consumption_kwh || 0), 0),
    excise: filteredAndSorted.reduce((s, p) => s + (p.annual_excise_amount || 0), 0),
    verified: filteredAndSorted.filter(p => p.verified_rcu).length,
  }), [filteredAndSorted]);

  useEffect(() => { setCurrentPage(1); }, [searchTerm, filterProvince, filterUsage]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="inline-block h-12 w-12 animate-spin rounded-full border-4 border-solid border-[#0A2540] border-r-transparent"></div>
      </div>
    );
  }

  return (
    <div className="max-w-[1400px] mx-auto space-y-4" data-testid="pod-active-page">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-4xl font-extrabold tracking-tighter text-slate-900">POD Attivi</h1>
          <p className="mt-1 text-sm text-slate-600">
            {pods.length} POD totali &middot; {provinces.length} province &middot; {totals.verified} verificati RCU
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant="outline" onClick={handleExport} className="rounded-sm text-xs" data-testid="export-pods-btn">
            <DownloadSimple size={14} className="mr-1" /> Esporta CSV
          </Button>
          <Button size="sm" variant="outline" onClick={fetchHistory} className="rounded-sm text-xs" data-testid="import-history-btn">
            <ArrowsClockwise size={14} className="mr-1" /> Storico Import
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-white border border-slate-200 rounded-sm p-4">
          <p className="text-xs text-slate-500 mb-1">POD Filtrati</p>
          <p className="text-2xl font-bold text-slate-900">{filteredAndSorted.length}</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-sm p-4">
          <p className="text-xs text-slate-500 mb-1">Consumo Annuo kWh</p>
          <p className="text-2xl font-bold text-slate-900">{totals.consumption.toLocaleString('it-IT', {maximumFractionDigits: 0})}</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-sm p-4">
          <p className="text-xs text-slate-500 mb-1">Accise Annue</p>
          <p className="text-2xl font-bold text-slate-900">EUR {totals.excise.toLocaleString('it-IT', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-sm p-4">
          <p className="text-xs text-slate-500 mb-1">Verificati RCU</p>
          <p className="text-2xl font-bold text-slate-900">{totals.verified} / {filteredAndSorted.length}</p>
        </div>
      </div>

      {showHistory && (
        <div className="bg-white border border-slate-200 rounded-sm p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-bold text-slate-900">Storico Caricamenti</h3>
            <button onClick={() => setShowHistory(false)} className="text-slate-400 hover:text-slate-600"><X size={16}/></button>
          </div>
          {importHistory.length === 0 ? (
            <p className="text-xs text-slate-500">Nessun caricamento trovato</p>
          ) : (
            <div className="space-y-2">
              {importHistory.map((batch) => (
                <div key={batch.batch_id} className="flex items-center justify-between p-3 bg-slate-50 rounded-sm border border-slate-100">
                  <div>
                    <p className="text-sm font-medium text-slate-900">{batch.filename}</p>
                    <p className="text-xs text-slate-500">{batch.count} POD &middot; {new Date(batch.imported_at).toLocaleDateString('it-IT', {day:'2-digit', month:'2-digit', year:'numeric', hour:'2-digit', minute:'2-digit'})}</p>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => handleRollback(batch.batch_id, batch.filename)}
                    className="rounded-sm text-xs text-red-600 border-red-200 hover:bg-red-50" data-testid={`rollback-${batch.batch_id}`}>
                    <Trash size={14} className="mr-1" /> Annulla Import
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input type="text" placeholder="Cerca POD, cliente, comune..." value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-9 pr-4 py-2 border border-slate-200 rounded-sm text-sm focus:outline-none focus:ring-1 focus:ring-[#0066FF]"
            data-testid="pod-search" />
        </div>
        <select value={filterProvince} onChange={(e) => setFilterProvince(e.target.value)}
          className="px-3 py-2 border border-slate-200 rounded-sm text-sm" data-testid="filter-province">
          <option value="">Tutte le province</option>
          {provinces.map(p => <option key={p} value={p}>{p}</option>)}
        </select>
        <select value={filterUsage} onChange={(e) => setFilterUsage(e.target.value)}
          className="px-3 py-2 border border-slate-200 rounded-sm text-sm" data-testid="filter-usage">
          <option value="">Tutte le tipologie</option>
          <option value="altri_usi">Altri usi</option>
          <option value="domestico">Domestico</option>
        </select>
        {(filterProvince || filterUsage || searchTerm) && (
          <button onClick={() => { setFilterProvince(''); setFilterUsage(''); setSearchTerm(''); }}
            className="text-xs text-slate-500 hover:text-slate-700 underline">Reset filtri</button>
        )}
      </div>

      <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
        <table className="w-full text-sm text-left" data-testid="pods-table">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="px-3 py-2.5 text-slate-600 font-semibold text-xs text-center">RCU</th>
              <SortHeader label="Codice POD" field="pod_code" sortConfig={sortConfig} onSort={handleSort} />
              <SortHeader label="Cliente" field="customer_name" sortConfig={sortConfig} onSort={handleSort} />
              <SortHeader label="Prov." field="province" sortConfig={sortConfig} onSort={handleSort} />
              <SortHeader label="Comune" field="comune" sortConfig={sortConfig} onSort={handleSort} />
              <SortHeader label="Indirizzo" field="indirizzo" sortConfig={sortConfig} onSort={handleSort} />
              <SortHeader label="Uso" field="usage_type" sortConfig={sortConfig} onSort={handleSort} />
              <SortHeader label="Data Inizio" field="activation_date" sortConfig={sortConfig} onSort={handleSort} align="center" />
              <SortHeader label="Consumo kWh" field="annual_consumption_kwh" sortConfig={sortConfig} onSort={handleSort} align="right" />
              <SortHeader label="Aliquota" field="excise_rate" sortConfig={sortConfig} onSort={handleSort} align="right" />
              <SortHeader label="Accisa Annua" field="annual_excise_amount" sortConfig={sortConfig} onSort={handleSort} align="right" />
              <th className="px-3 py-2.5 text-slate-600 font-semibold text-xs text-center">Stato Com.</th>
              <th className="px-3 py-2.5 text-slate-600 font-semibold text-xs text-center">Azioni</th>
            </tr>
          </thead>
          <tbody>
            {paginatedPods.map((pod) => (
              <tr key={pod.id} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                <td className="px-3 py-2 text-center">
                  <button onClick={() => handleToggleRCU(pod.id)} className="p-0.5" data-testid={`rcu-toggle-${pod.pod_code}`}>
                    {pod.verified_rcu ? (
                      <CheckCircle size={18} weight="fill" className="text-green-600" />
                    ) : (
                      <XCircle size={18} className="text-slate-300 hover:text-amber-500 transition-colors" />
                    )}
                  </button>
                </td>
                <td className="px-3 py-2 font-mono text-xs text-slate-900 whitespace-nowrap">{pod.pod_code}</td>
                <td className="px-3 py-2 text-xs text-slate-700 max-w-[160px] truncate">{pod.customer_name}</td>
                <td className="px-3 py-2">
                  <span className="px-1.5 py-0.5 bg-slate-100 text-slate-700 rounded-sm text-xs font-medium">{pod.province}</span>
                </td>
                <td className="px-3 py-2 text-xs text-slate-600 whitespace-nowrap">{pod.comune || pod.city || '-'}</td>
                <td className="px-3 py-2 text-xs text-slate-600 max-w-[160px] truncate">
                  {[pod.toponimo, pod.indirizzo, pod.civico].filter(Boolean).join(' ') || '-'}
                </td>
                <td className="px-3 py-2">
                  <span className={`text-xs font-medium px-1.5 py-0.5 rounded-sm ${
                    pod.usage_type === 'domestico' ? 'bg-blue-50 text-blue-700' : 'bg-amber-50 text-amber-700'
                  }`}>
                    {pod.usage_type === 'domestico' ? 'Dom.' : 'Altri'}
                  </span>
                </td>
                <td className="px-3 py-2 text-center text-xs text-slate-700 whitespace-nowrap">
                  {pod.activation_date ? new Date(pod.activation_date).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '-'}
                </td>
                <td className="px-3 py-2 text-right text-xs text-slate-700 font-mono whitespace-nowrap">
                  {(pod.annual_consumption_kwh || 0).toLocaleString('it-IT', {maximumFractionDigits: 2})}
                </td>
                <td className="px-3 py-2 text-right text-xs text-slate-700 font-mono">
                  {(pod.excise_rate || 0).toFixed(4)}
                </td>
                <td className="px-3 py-2 text-right text-xs font-medium text-slate-900 font-mono whitespace-nowrap">
                  {(pod.annual_excise_amount || 0).toLocaleString('it-IT', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                </td>
                <td className="px-3 py-2 text-center">
                  {pod.communication_sent ? (
                    <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-green-700 bg-green-50 px-1.5 py-0.5 rounded-sm border border-green-200" data-testid={`comm-badge-${pod.pod_code}`}>
                      <CheckCircle size={12} weight="fill" /> Inviata
                    </span>
                  ) : (
                    <span className="text-[10px] text-slate-400">-</span>
                  )}
                </td>
                <td className="px-3 py-2 text-center">
                  <button onClick={() => openEdit(pod)} className="p-1 hover:bg-slate-100 rounded transition-colors" data-testid={`edit-pod-${pod.pod_code}`}>
                    <PencilSimple size={14} className="text-slate-500" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-xs text-slate-500">Pagina {currentPage} di {totalPages} &middot; {filteredAndSorted.length} POD</p>
          <div className="flex gap-1">
            <Button size="sm" variant="outline" disabled={currentPage === 1}
              onClick={() => setCurrentPage(p => p - 1)} className="rounded-sm h-8 w-8 p-0">
              <CaretLeft size={14} />
            </Button>
            {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
              let page;
              if (totalPages <= 7) page = i + 1;
              else if (currentPage <= 4) page = i + 1;
              else if (currentPage > totalPages - 4) page = totalPages - 6 + i;
              else page = currentPage - 3 + i;
              return (
                <Button key={page} size="sm" variant={currentPage === page ? 'default' : 'outline'}
                  onClick={() => setCurrentPage(page)}
                  className={`rounded-sm h-8 w-8 p-0 text-xs ${currentPage === page ? 'bg-[#0A2540]' : ''}`}>
                  {page}
                </Button>
              );
            })}
            <Button size="sm" variant="outline" disabled={currentPage === totalPages}
              onClick={() => setCurrentPage(p => p + 1)} className="rounded-sm h-8 w-8 p-0">
              <CaretRight size={14} />
            </Button>
          </div>
        </div>
      )}

      {editPod && (
        <EditPodModal
          editPod={editPod}
          editForm={editForm}
          setEditForm={setEditForm}
          saving={saving}
          onSave={handleSaveEdit}
          onClose={() => setEditPod(null)}
        />
      )}
    </div>
  );
};

export default PODActivePage;
